import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Box, useTheme } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PositionLeg } from 'types/optionsCalculator';
import OutOfViewStrikeIndicator from './StrikeManagement/OutOfViewStrikeIndicator';
import StrikeSlot from './StrikeManagement/StrikeSlot';
import SelectedStrike from './StrikeManagement/SelectedStrike';

interface StrikePickerProps {
  strikes: number[];
  currentLegs: PositionLeg[];
  onStrikeChange: (legId: string, newStrike: number) => void;
  onStrikeAdd?: (strike: number) => void;
  onStrikeRemove?: (strike: number) => void;
}

const StrikePicker: React.FC<StrikePickerProps> = ({
  strikes,
  currentLegs,
  onStrikeChange,
  onStrikeAdd,
  onStrikeRemove,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [domainOffset, setDomainOffset] = useState(0);
  const [containerWidth, setContainerWidth] = useState(800);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, offset: 0 });
  const [activeLeg, setActiveLeg] = useState<PositionLeg | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Memoized calculations to prevent unnecessary recalculations
  const {
    strikeSpacing,
    visibleStrikes,
    visiblePositions,
    outOfViewLegs,
    strikeLegsMap,
  } = useMemo(() => {
    const minStrikeSpacing = 40;
    const maxVisibleStrikes = Math.floor(containerWidth / minStrikeSpacing);
    const spacing = containerWidth / (maxVisibleStrikes - 1);

    // Calculate center and visible range
    const centerStrike =
      currentLegs.length > 0
        ? currentLegs.reduce((sum, leg) => sum + leg.strike, 0) /
          currentLegs.length
        : strikes[Math.floor(strikes.length / 2)] || 100;

    const centerIndex = strikes.findIndex((strike) => strike >= centerStrike);
    const adjustedCenterIndex =
      centerIndex === -1 ? Math.floor(strikes.length / 2) : centerIndex;

    const halfRange = Math.floor(maxVisibleStrikes / 2);
    const offsetIndex = Math.floor(domainOffset / spacing);
    const startIndex = Math.max(
      0,
      Math.min(
        adjustedCenterIndex - halfRange + offsetIndex,
        strikes.length - maxVisibleStrikes,
      ),
    );

    const visible = strikes.slice(startIndex, startIndex + maxVisibleStrikes);
    const positions = visible.map(
      (_, index) => index * spacing - (domainOffset % spacing),
    );

    // Create a map of strikes to their legs
    const legsMap = new Map<number, PositionLeg[]>();
    currentLegs.forEach((leg) => {
      if (!legsMap.has(leg.strike)) {
        legsMap.set(leg.strike, []);
      }
      legsMap.get(leg.strike)!.push(leg);
    });

    // Find out-of-view legs with improved tracking
    const visibleStrikeSet = new Set(visible);
    const outOfView = currentLegs.filter(
      (leg) => !visibleStrikeSet.has(leg.strike),
    );

    // Separate left and right out-of-view legs
    const minVisibleStrike = Math.min(...visible);
    const maxVisibleStrike = Math.max(...visible);

    const leftOutOfView = outOfView.filter(
      (leg) => leg.strike < minVisibleStrike,
    );
    const rightOutOfView = outOfView.filter(
      (leg) => leg.strike > maxVisibleStrike,
    );

    // Group by strike for better organization
    const leftStrikes = [...new Set(leftOutOfView.map((leg) => leg.strike))];
    const rightStrikes = [...new Set(rightOutOfView.map((leg) => leg.strike))];

    // Find closest strikes for each direction
    const closestLeftStrike =
      leftStrikes.length > 0 ? Math.max(...leftStrikes) : null;
    const closestRightStrike =
      rightStrikes.length > 0 ? Math.min(...rightStrikes) : null;

    // Find the closest leg for each direction
    const closestLeftLeg = closestLeftStrike
      ? leftOutOfView.find((leg) => leg.strike === closestLeftStrike)
      : null;
    const closestRightLeg = closestRightStrike
      ? rightOutOfView.find((leg) => leg.strike === closestRightStrike)
      : null;

    return {
      strikeSpacing: spacing,
      visibleStrikes: visible,
      visiblePositions: positions,
      strikeLegsMap: legsMap,
      outOfViewLegs: {
        left: leftOutOfView,
        right: rightOutOfView,
        leftStrikes,
        rightStrikes,
        closestLeftStrike,
        closestRightStrike,
        closestLeftLeg,
        closestRightLeg,
      },
    };
  }, [strikes, currentLegs, containerWidth, domainOffset]);

  // Memoized strike slots with integrated legs
  const strikeSlots = useMemo(() => {
    return visibleStrikes.map((strike, index) => (
      <StrikeSlot
        key={`slot-${strike}`}
        strike={strike}
        position={visiblePositions[index]}
        isOver={overId === `slot-${strike}`}
        legs={strikeLegsMap.get(strike) || []}
      />
    ));
  }, [visibleStrikes, visiblePositions, overId, strikeLegsMap]);

  // Memoized out-of-view indicators
  const outOfViewIndicators = useMemo(() => {
    const indicators: React.ReactNode[] = [];

    if (strikes.length === 0) {
      return null;
    }

    // Left indicators (strikes lower than visible range)
    if (
      outOfViewLegs.leftStrikes.length > 0 &&
      outOfViewLegs.closestLeftStrike &&
      outOfViewLegs.closestLeftLeg
    ) {
      indicators.push(
        <OutOfViewStrikeIndicator
          key="left-indicator"
          closestStrike={outOfViewLegs.closestLeftStrike}
          closestLeg={outOfViewLegs.closestLeftLeg}
          allStrikes={outOfViewLegs.leftStrikes}
          allLegs={outOfViewLegs.left}
          theme={theme}
          direction="left"
          containerWidth={containerWidth}
        />,
      );
    }

    // Right indicators (strikes higher than visible range)
    if (
      outOfViewLegs.rightStrikes.length > 0 &&
      outOfViewLegs.closestRightStrike &&
      outOfViewLegs.closestRightLeg
    ) {
      indicators.push(
        <OutOfViewStrikeIndicator
          key="right-indicator"
          closestStrike={outOfViewLegs.closestRightStrike}
          closestLeg={outOfViewLegs.closestRightLeg}
          allStrikes={outOfViewLegs.rightStrikes}
          allLegs={outOfViewLegs.right}
          theme={theme}
          direction="right"
          containerWidth={containerWidth}
        />,
      );
    }

    return indicators;
  }, [outOfViewLegs, theme, containerWidth, strikes.length]);

  // Simplified resize handler with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateContainerWidth = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setContainerWidth(rect.width);
        }
      }, 100);
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
      clearTimeout(timeoutId);
    };
  }, []);

  // Simplified drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'leg') {
      setActiveLeg(active.data.current.leg as PositionLeg);
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    setOverId((over?.id as string) || null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveLeg(null);
      setOverId(null);

      if (!over || active.id === over.id) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      // Handle leg being dropped on a slot
      if (activeData?.type === 'leg' && overData?.type === 'slot') {
        const legId = active.id as string;
        const newStrike = overData.strike as number;

        // Allow movement to any strike in the strikes array
        if (strikes.includes(newStrike)) {
          onStrikeChange(legId, newStrike);
        }
      }
    },
    [strikes, onStrikeChange],
  );

  // Simplified mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setIsDragging(true);
      setDragStart({
        x: e.clientX - rect.left,
        offset: domainOffset,
      });
    },
    [domainOffset],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const deltaX = currentX - dragStart.x;

      setDomainOffset(dragStart.offset - deltaX);
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const adjustedX = clickX + (domainOffset % strikeSpacing);
      const strikeIndex = Math.round(adjustedX / strikeSpacing);
      const clickedStrike = visibleStrikes[strikeIndex];

      if (clickedStrike !== undefined) {
        if (currentLegs.some((leg) => leg.strike === clickedStrike)) {
          onStrikeRemove?.(clickedStrike);
        } else {
          onStrikeAdd?.(clickedStrike);
        }
      }
    },
    [
      domainOffset,
      strikeSpacing,
      visibleStrikes,
      currentLegs,
      onStrikeAdd,
      onStrikeRemove,
    ],
  );

  // Event listeners for domain dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        py: '24px',
        pb: '48px', // Extra padding for out-of-view indicators
        position: 'relative',
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: 90,
            position: 'relative',
            cursor: isDragging ? 'grabbing' : 'grab',
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 1,
            backgroundColor: theme.palette.background.default,
            userSelect: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: `linear-gradient(to right, ${theme.palette.background.paper}, transparent)`,
              zIndex: 2,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background: `linear-gradient(to left, ${theme.palette.background.paper}, transparent)`,
              zIndex: 2,
              pointerEvents: 'none',
            },
          }}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
        >
          <SortableContext
            items={visibleStrikes.map((strike) => `slot-${strike}`)}
            strategy={horizontalListSortingStrategy}
          >
            {strikeSlots}
          </SortableContext>

          <DragOverlay>
            {activeLeg
              ? (() => {
                  const hoveredStrike =
                    overId && !isNaN(Number(overId?.split('-')[1]))
                      ? Number(overId?.split('-')[1])
                      : activeLeg.strike;
                  return (
                    <SelectedStrike
                      strike={hoveredStrike}
                      type={activeLeg.type}
                      quantity={activeLeg.quantity}
                      expiration={activeLeg.expiration}
                    />
                  );
                })()
              : null}
          </DragOverlay>
        </Box>

        {/* Out-of-view indicators */}
        {outOfViewIndicators}
      </DndContext>
    </Box>
  );
};

export default StrikePicker;
